/**
 * Admin auth header helper — Wave 13 (admin-auth-hardening).
 *
 * The typed admin clients used to unconditionally attach
 *   `x-config-seed-bypass: <bypassKey>`
 * to every write request. Wave 13 introduces token-based role-claim auth on
 * the Function side: `AdminAuthService` accepts EITHER a bearer token whose
 * role claim contains the required role OR (legacy) the bypass-key header.
 *
 * After Wave 13 the typed admin clients pass `bypassKey` through
 * `buildAdminAuthHeaders()`. When the value is empty/null the bypass-key
 * header is OMITTED, and the http transport's auto-injected `Authorization:
 * Bearer <jwt>` becomes the sole auth carrier. Callers that still possess a
 * bypass-key (legacy seed scripts, super-admin-only ops) may pass a non-empty
 * string and the server treats it as a fallback.
 *
 * Frontend callers that want the prefer-token path should call
 * `getAdminAuthCredential()` from `@retail-ops/api-client` instead of
 * `promptBypassKey()`. When a bearer token is available the helper returns
 * an empty string sentinel; otherwise it falls back to the legacy prompt.
 */

import type { RequestOptions } from '@retail-ops/shared-types';

/**
 * Sentinel value the ROP-teams `promptBypassKey()` helper returns when a
 * Teams SSO bearer token is available — meaning the caller wants the
 * server-side AdminAuthService to authorize via role-claim, not bypass-key.
 *
 * Kept as a non-empty string so legacy editor code (`if (!bypass) return;`)
 * still passes the truthy-check; the value is filtered out here before the
 * header lands on the wire.
 */
export const ADMIN_AUTH_TOKEN_SENTINEL = '__token__';

/**
 * Build the headers map for an admin write call. When `bypassKey` is empty
 * OR the Wave 13 token sentinel, the `x-config-seed-bypass` header is
 * omitted entirely so the server-side AdminAuthService falls back to the
 * bearer-token-only path.
 */
export function buildAdminAuthHeaders(
  callerHeaders: Record<string, string> | undefined,
  bypassKey: string | null | undefined,
): Record<string, string> {
  const out: Record<string, string> = { ...(callerHeaders ?? {}) };
  if (typeof bypassKey === 'string') {
    const trimmed = bypassKey.trim();
    if (trimmed.length > 0 && trimmed !== ADMIN_AUTH_TOKEN_SENTINEL) {
      out['x-config-seed-bypass'] = bypassKey;
    }
  }
  return out;
}

/**
 * Apply admin-auth headers to a RequestOptions-like bag.
 */
export function withAdminAuth<T extends RequestOptions>(
  opts: T | undefined,
  bypassKey: string | null | undefined,
): T {
  return {
    ...(opts ?? ({} as T)),
    headers: buildAdminAuthHeaders(opts?.headers, bypassKey),
  } as T;
}
